<template>
  <div>
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t('selection.noOption') }}
              </template>
            </v-select>
          </b-col>
          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
          >
            <b-button
              variant="primary"
              @click="showModal"
            >
              {{ $t('button.add_employee') }}
            </b-button>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="false"
          :updatable="false"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
        >
          <template #cell(name)="data">
            {{ data.item.name || '-' }}
            <!-- {{ data.item.operator.firstName }} {{ data.item.operator.lastName }} -->
          </template>
          <template #cell(email)="data">
            {{ data.item.email || '-' }}
          </template>
          <template #cell(phone)="data">
            {{ data.item.phone || '-' }}
          </template>
          <template #cell(team)="data">
            <template v-if="data.item.teams.length > 0">
              <b-badge
                v-for="team in data.item.teams"
                :key="team.id"
                variant="primary"
                pill
                style="margin-right: 0.5rem;"
                badge-glow
              >
                {{ getTeamName(team) }}
              </b-badge>
            </template>
            <span v-else>-</span>
          </template>
          <!-- <template #cell(status)="data">
            <b-badge
              :variant="data.item.isActive ? 'success' : 'danger'"
            >
              {{ data.item.isActive ? $t('general.active') : $t('general.inactive') }}
            </b-badge>
          </template> -->
          <template #cell(status)="data">
            <b-badge
              v-if="data.item.isActive == 1"
              pill
              variant="success"
              badge-glow
            >
              {{ $t(`status.${data.item.isActive}`) }}
            </b-badge>
            <b-badge
              v-else-if="data.item.isActive == 2 && data.item.phone"
              pill
              variant="warning"
              badge-glow
            >
              {{ $t(`status.pending`) }}
            </b-badge>
            <b-badge
              v-else-if="data.item.isActive == 2"
              pill
              variant="info"
              badge-glow
            >
              {{ $t(`status.${data.item.isActive}`) }}
            </b-badge>
            <b-badge
              v-else
              pill
              variant="danger"
              badge-glow
            >
              {{ $t(`status.${data.item.isActive}`) }}
            </b-badge>
          </template>
          <template #cell(role)="data">
            {{ data.item.isLeader ? $t('teamMemberRole.leader') : $t('teamMemberRole.member') }}
          </template>
          <template #front-button="data">
            <b-button
              v-b-tooltip.hover
              :title="$t('button.remove')"
              variant="danger"
              size="sm"
              pill
              @click="removeMember(data.item.id, data.index, data.item)"
            >
              <feather-icon icon="UserMinusIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>

    <b-modal
      v-model="isModalVisible"
      :title="modalTitle"
      hide-footer
      size="md"
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <validation-observer
        ref="formValidation"
        #default="{ invalid }"
      >
        <validation-provider
          v-slot="{ errors }"
          :name="$t('breadcrumb.employee')"
          :rules="isEditing ? '' : 'required'"
        >
          <b-form-group
            :label="$t('breadcrumb.employee')"
            label-for="employee"
          >
            <n-async-single-select
              v-model="form.merchantUserId"
              repository="employeeDepartment"
              selection-key="id"
              :selection-label="['name', 'role']"
              :clearable="false"
              :state="errors.length > 0 ? false : null"
              :readonly="isEditing"
              :query="{
                departmentId: departmentId,
              }"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <b-row class="mt-3">
          <b-col class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="isModalVisible = false"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="invalid || submitting"
              @click="onSubmit"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm mr-1"
              />
              {{ $t('button.add') }}
            </b-button>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BModal,
  BFormGroup,
  BBadge,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination.vue'
import NTable from '@/components/NTable.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import vSelect from 'vue-select'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

const TeamMemberRepository = Repository.get('operator')
const EmployeeDepartmentRepository = Repository.get('employeeDepartment')

export default {
  name: 'EmployeeList',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BModal,
    BFormGroup,
    NPagination,
    NTable,
    ValidationProvider,
    ValidationObserver,
    NSearchContainer,
    NSearchInput,
    NAsyncSingleSelect,
    vSelect,
    BBadge,
    FeatherIcon,
  },
  props: {
    teamId: {
      type: [String, Number],
      default: null,
    },
    departmentId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        name: this.$route.query.name || null,
        email: this.$route.query.email || null,
        phone: this.$route.query.phone || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        teamId: this.teamId,
        departmentId: this.departmentId,
      },
      isModalVisible: false,
      form: {
        merchantUserId: null,
        teamId: null,
        isLeader: false,
        isActive: true,
      },
      selectedItem: null,
      submitting: false,
      locale: this.$i18n.locale.split('-')[0],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
      selectedEmployee: null,
    }
  },
  computed: {
    modalTitle() {
      return this.$t('button.add_employee')
    },
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
    '$i18n.locale': function (val) {
      const [locale] = val.split('-')
      this.locale = locale
    },
    teamId: {
      handler(val) {
        this.query.teamId = val
        this.getData()
      },
      immediate: true,
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    getTeamName(team) {
      return team.name[this.locale] || team.name.en || team.name.zh || team.name.km || team.name.vi || '-'
    },
    updateQueryString() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQueryString()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQueryString()
      this.getData()
    },
    getData() {
      this.loading = true
      TeamMemberRepository.index({
        ...this.query,
        departmentId: this.$route.params.departmentId,
      })
        .then(response => {
          const { list } = response.data.data
          this.items = [...list]
          this.total = response.data.data.total
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    showModal() {
      this.isModalVisible = true
      this.isEditing = false
      if (this.teamId) {
        this.form.teamId = this.teamId
      }
    },
    editItem(item) {
      this.selectedItem = {
        id: item.id,
        teamId: this.teamId,
        isLeader: item.isLeader ? 'true' : 'false',
        merchantUserId: item.id,
        isActive: item.isActive,
      }
      this.selectedEmployee = item
      this.form = {
        ...this.selectedItem,
        teamId: this.teamId,
      }
      this.isEditing = true
      this.isModalVisible = true
    },
    async destroy(id) {
      await EmployeeDepartmentRepository.destroyEmployee(this.departmentId, id)
      this.getData()
    },
    async onSubmit() {
      const isValid = await this.$refs.formValidation.validate()
      if (!isValid || this.submitting) return

      this.submitting = true
      const payload = {
        departmentId: this.departmentId,
        merchantUserId: this.form.merchantUserId,
      }

      try {
        await EmployeeDepartmentRepository.addEmployee(payload)
        this.getData()
        this.isModalVisible = false
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.formValidation.setErrors(error.response.data.message)
        }
      } finally {
        this.submitting = false
      }
    },
    resetForm() {
      this.form = {
        merchantUserId: null,
        teamId: this.teamId,
        isActive: true,
      }
      this.selectedItem = null
      this.selectedEmployee = null
      if (this.$refs.formValidation) {
        this.$refs.formValidation.reset()
      }
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQueryString()
      this.getData()
    },
    formatErrorMessage(message) {
      return message ? this.$t(message) : ''
    },
    getNameByLang(data) {
      if (!data.name) return ''

      if (this.$i18n.locale === 'vi') {
        return data.name.vi ? data.name.vi : data.name.en
      }
      if (this.$i18n.locale === 'km') {
        return data.name.km ? data.name.km : data.name.en
      }
      if (this.$i18n.locale === 'en') {
        return data.name.en ? data.name.en : data.name.zh
      }
      return data.name.zh ? data.name.zh : data.name.en
    },
    removeMember(id, index, data) {
      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.areYouSureToRemoveEmployee', {
            employee: data.firstName || data.lastName ? `${data.firstName} ${data.lastName}` : '-',
          }),
          {
            title: this.$t('alert.confirmContinueOperation'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.confirm'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.destroy(id)
          }
        })
    },
  },
  setup() {
    const searchFields = [
      {
        key: 'name',
        label: 'field.name',
        type: 'text',
        sm: 12,
        md: 6,
        lg: 4,
      },
      {
        key: 'teamId',
        label: 'breadcrumb.team',
        repository: 'team',
        type: 'async-single-selection',
        selectionLabel: 'translations',
        selectionKey: 'id',
        isCustomParams: true,
        sm: 12,
        md: 6,
        lg: 4,
      },
    ]

    const fields = [
      {
        key: 'name',
        label: 'field.name',
        sortable: true,
      },
      {
        key: 'team',
        label: 'breadcrumb.team',
      },
      {
        key: 'email',
        label: 'field.email',
      },
      {
        key: 'phone',
        label: 'field.phone',
      },
      // {
      //   key: 'email',
      //   label: 'field.email',
      //   sortable: false,
      // },
      // {
      //   key: 'role',
      //   label: 'field.role',
      //   sortable: false,
      // },
      {
        key: 'status',
        label: 'field.status',
        sortable: false,
      },
      {
        key: 'serviceCap',
        label: 'general.chat_cap',
        variant: 'center',
      },
      {
        key: 'totalServiceCap',
        label: 'field.serviceCap',
        variant: 'center',
      },
      {
        key: 'totalServiceWork',
        label: 'general.total_service_work',
        variant: 'center',
      },
      // {
      //   key: 'createdAt',
      //   label: 'general.assignAt',
      //   sortable: true,
      // },
      {
        key: 'actions',
        label: 'general.action',
      },
    ]

    const permissionType = 'manage-employee'
    const route = 'employee'

    return {
      permissionType,
      route,
      fields,
      searchFields,
    }
  },
}
</script>
